import { ReactNode, useState } from "react";
import { FormattedMessage, defineMessages } from "react-intl";
import FormGroup from "../FormGroup";
import MarkdownEditor from "../MarkdownEditor";
import {
  MarkdownEditorContextProps,
  MarkdownValue,
} from "../MarkdownEditor/context";
import { MarkdownEditorProvider } from "../MarkdownEditor/Provider";
import {
  BaseProjectVersionApprovalButton,
  FragmentKey,
  Messages,
} from "./Base";

const messages = defineMessages<keyof Messages>({
  dialogTriggerCreate: { defaultMessage: "Reject" },
  dialogTriggerUpdate: { defaultMessage: "Rejected" },
  dialogTitle: { defaultMessage: `Reject submission "{version}" of {entity}` },
  createSubmit: { defaultMessage: "Reject" },
  onCreateSuccess: {
    defaultMessage: "{entity}'s submission has been rejected!",
  },
  onCreateFailure: {
    defaultMessage:
      "{entity}'s submission cannot be rejected at the moment, please retry later.",
  },
  updateSubmit: { defaultMessage: "Update rejection" },
  onUpdateSuccess: {
    defaultMessage: "{entity}'s submission rejection has been updated!",
  },
  onUpdateFailure: {
    defaultMessage:
      "{entity}'s submission rejection cannot be updated at the moment, please retry later.",
  },
  deleteSubmit: { defaultMessage: "Withdraw rejection" },
  onDeleteSuccess: {
    defaultMessage:
      "You have just withdrawn your rejection on {entity}'s submission",
  },
  onDeleteFailure: {
    defaultMessage:
      "{entity}'s submission rejection cannot be withdrawn at the moment, please retry later.",
  },
});

interface FormData {
  reason: string;
}

const errorMessages: { [K in keyof FormData]: Record<string, ReactNode> } = {
  reason: {},
};

interface Props {
  projectVersion: FragmentKey;
}

export default function ProjectVersionRejectButton(props: Props) {
  const [reason, setReason] = useState<MarkdownValue>("");

  return (
    <BaseProjectVersionApprovalButton<FormData>
      fragment={props.projectVersion}
      messages={messages}
      isHidden={({ approval }) => !!approval && approval.accept}
      getDefaultValues={({ approval }) => ({
        reason: approval?.notes ?? undefined,
      })}
      getCreateInput={(data) => ({
        reject: {
          reason: data.reason,
        },
      })}
      getUpdateInput={(data) => ({
        reject: {
          reason: data.reason,
        },
      })}
    >
      {({ formState, ...form }) => {
        const markdownContext: MarkdownEditorContextProps<FormData> = {
          name: "reason",
          control: form.control,
          defaultValue: "",
          setMarkdownValue: setReason,
          markdownValue: reason,
          canUploadFiles: false,
          rules: { maxLength: 2000 },
        };

        return (
          <>
            <MarkdownEditorProvider value={markdownContext}>
              <FormGroup
                label={<FormattedMessage defaultMessage="Notes" />}
                error={
                  typeof formState.errors.reason?.type === "string" &&
                  errorMessages.reason[formState.errors.reason.type]
                }
              >
                <MarkdownEditor
                  rows={5}
                  aria-invalid={formState.errors.reason ? "true" : "false"}
                />
              </FormGroup>
            </MarkdownEditorProvider>
          </>
        );
      }}
    </BaseProjectVersionApprovalButton>
  );
}
